import { z } from 'zod';

/**
 * Push analytics (BB-79): counts per push delivery (a send to one device), like Braze's Message Performance, over the
 * facts of BB-72/BB-79. `basis: send_cohort` (default) counts every fact on the day of its send, so opens are compared
 * with the sends they came from; `event_day` counts facts on the day they happened. Test and control facts are left out.
 */
export const PUSH_ANALYTICS_GROUPS = ['platform', 'day', 'variant'] as const;
export const PUSH_ANALYTICS_BASES = ['send_cohort', 'event_day'] as const;

const Day = z.iso.date();

export const PushAnalyticsQuery = z
  .object({
    campaignId: z.uuid().optional(),
    canvasId: z.uuid().optional(),
    canvasStepId: z.uuid().optional(),
    from: Day,
    to: Day,
    groupBy: z.enum(PUSH_ANALYTICS_GROUPS).default('platform'),
    basis: z.enum(PUSH_ANALYTICS_BASES).default('send_cohort'),
  })
  .refine((q) => q.from <= q.to, { message: 'from must not be after to', path: ['from'] })
  .refine((q) => (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 366, { message: 'at most 366 days', path: ['to'] });
export type PushAnalyticsQuery = z.infer<typeof PushAnalyticsQuery>;

/** Raw counts of one row; every metric is a formula over them. */
export interface PushCounts {
  /** Handed to the provider: accepted, rejected by the provider, or no answer (pre-send failures and expiries are not sends). */
  sends: number;
  accepted: number;
  /** Provider said the token is invalid or unregistered. */
  bounces: number;
  unknown: number;
  /** SDK receipts (`delivered`). */
  received: number;
  directOpens: number;
  influencedOpens: number;
  bodyClicks: number;
  /** Taps per button id (dismissive buttons included). */
  buttonClicks: Record<string, number>;
  dismissed: number;
  uniqueRecipients: number;
}

export const PUSH_METRIC_KEYS = [
  'sends',
  'accepted',
  'accepted_rate',
  'bounces',
  'bounce_rate',
  'received',
  'received_rate',
  'direct_opens',
  'direct_open_rate',
  'influenced_opens',
  'influenced_open_rate',
  'total_opens',
  'total_open_rate',
  'body_clicks',
  'unique_recipients',
] as const;
export type PushMetricKey = (typeof PUSH_METRIC_KEYS)[number];

export interface PushMetricDefinition {
  key: PushMetricKey;
  title: { uk: string; en: string };
  format: 'count' | 'percent';
  /** The tooltip: what the number counts, as computed. */
  formula: { uk: string; en: string };
  /** Where the number is incomplete (iOS receipts). */
  coverage?: { uk: string; en: string };
}

export interface PushAnalyticsRow {
  key: { platform?: string; day?: string; variant?: string | null };
  counts: PushCounts;
  /** Rates are fractions (0–1), `null` when the denominator is 0. */
  metrics: Record<PushMetricKey, number | null>;
}

export interface PushAnalyticsResponse {
  basis: (typeof PUSH_ANALYTICS_BASES)[number];
  groupBy: (typeof PUSH_ANALYTICS_GROUPS)[number];
  influencedOpenWindowMinutes: number;
  rows: PushAnalyticsRow[];
  total: PushAnalyticsRow;
  definitions: PushMetricDefinition[];
}

/** Why pushes did not go out or failed (BB-138): `suppressed` reasons and `failed` codes, by stage. */
export interface PushSuppressedResponse {
  suppressed: Array<{ reason: string; count: number }>;
  failed: Array<{ code: string; stage: string; count: number }>;
}
